/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Overlay Square opción B: bottom sheet en mobile · dialog paper centrado en desktop
 * (mismo contrato que FilterSheetModal appearance="ticket").
 */
import React from 'react';
import { Modal } from '../modals/Modal';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SoftPressable } from '../ui/SoftPressable';
import { spacing, ticketColors } from '../theme/public-api';
import { ticketChrome as ticketUi } from './ticketChrome.styles';
import { TicketModalHeader } from './TicketModalHeader';
import { ticketMoreActions as styles } from './ticketMoreActions.styles';

export type TicketMoreAction = {
  key: string;
  label: string;
  hint?: string;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  onPress: () => void;
  disabled?: boolean;
  /** testID para E2E (se aplica al botón de la fila). */
  testID?: string;
};

type Props = {
  visible: boolean;
  title: string;
  actions: TicketMoreAction[];
  onClose: () => void;
};

const WIDE_BREAKPOINT = 768;

/** Acciones secundarias — sheet mobile / dialog desk (Square B). */
export function TicketMoreActionsSheet({ visible, title, actions, onClose }: Props) {
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const asDialog = screenWidth >= WIDE_BREAKPOINT;
  const sheetMaxHeight = screenHeight * 0.92;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={asDialog ? 'fade' : 'slide'}
      onRequestClose={onClose}
    >
      <View style={[ticketUi.sheetBackdrop, asDialog && local.backdropDialog]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            asDialog ? ticketUi.dialogCard : styles.sheet,
            asDialog && local.dialogWidth,
            {
              paddingBottom: asDialog ? 12 : Math.max(insets.bottom, spacing.sm),
              maxHeight: sheetMaxHeight,
            },
          ]}
        >
          {!asDialog ? <View style={ticketUi.grabber} /> : null}

          <TicketModalHeader
            title={title}
            onClose={onClose}
            compact={!asDialog}
            borderless
          />

          <ScrollView
            style={{ maxHeight: screenHeight * (asDialog ? 0.62 : 0.58) }}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {actions.map((item) => (
              <SoftPressable
                key={item.key}
                style={[styles.row, item.disabled && styles.rowDisabled]}
                onPress={() => {
                  if (item.disabled) return;
                  onClose();
                  item.onPress();
                }}
                disabled={item.disabled}
                accessibilityRole="button"
                accessibilityLabel={item.label}
                accessibilityState={{ disabled: Boolean(item.disabled) }}
                testID={item.testID}
                hoverLift={!item.disabled}
              >
                <View style={styles.iconWrap}>
                  <Ionicons
                    name={item.icon}
                    size={20}
                    color={item.disabled ? ticketColors.ink3 : ticketColors.gold}
                  />
                </View>
                <View style={styles.textCol}>
                  <Text style={styles.rowLabel}>{item.label}</Text>
                  {item.hint ? <Text style={styles.rowHint}>{item.hint}</Text> : null}
                </View>
              </SoftPressable>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const local = StyleSheet.create({
  backdropDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  dialogWidth: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },
});
